import { Gym, GymTypes } from 'fitbeat.models';
import { WarmupWarmdownConfig } from '../../config/WarmupwarmdownConfig';
import { WorkoutPausesConfig } from '../../config/workoutpausesconfig';
import {
  ExerciseFormat,
  ExerciseSessionType,
  UserExerciseSession, UserWorkoutPause,
  UserWorkoutSession, WorkoutSettings,
} from '../../entities';
import { isStaticCardioZone } from '../../config';

export interface IWorkoutHydrationInfo {
  [workoutStage: string]: {
    zoneNumberInGym: number;
    indexOfZoneTypeInZoneNumber: number;
    useAlternativeExercises: boolean;
  };
}

interface INumberOfTimesZoneTypeHasBeenDoneState {
  [zoneType: string]: number;
}
interface INumberOfTimesZoneTypeHasBeenScheduledState {
  [zoneType: string]: number;
}
export class ScheduleExercises {
  public static setPauses(
    userWorkoutSession: UserWorkoutSession, pauses: UserWorkoutPause[], gymType: GymTypes = GymTypes.Indoor) {
    for (const exerciseSession of userWorkoutSession.exerciseSessions) {
      ScheduleExercises.setPauseIfMatchesConfig(exerciseSession, pauses, userWorkoutSession.settings, gymType);
    }
  }

  public static scheduleExercisesInWorkout(
    userWorkoutSession: UserWorkoutSession,
    startingStage: number,
    gymType: GymTypes,
    staticZoneNumber?: number,
  ): void {
    const cardioMachineZone = isStaticCardioZone(staticZoneNumber);
    const pauseConfig = WorkoutPausesConfig.pausesInWorkout(userWorkoutSession.workoutType, staticZoneNumber);

    for (let i = startingStage - 1; i < userWorkoutSession.workoutType; i++) {
      const stationNumber = NaN; // we do not have the station number yet, until the workout is scheduled
      const exerciseSession = UserExerciseSession.fromParentWorkoutSession(
        ExerciseSessionType.User,
        stationNumber,
        userWorkoutSession,
        i + 1,
        staticZoneNumber,
      );

      ScheduleExercises.setPauseIfMatchesConfig(exerciseSession, pauseConfig, userWorkoutSession.settings, gymType);

      ScheduleExercises.setWarmupWarmdown(
        exerciseSession,
        userWorkoutSession.workoutType,
        cardioMachineZone,
        userWorkoutSession.settings,
      );
      userWorkoutSession.exerciseSessions.push(exerciseSession);
    }
  }

  public static scheduleExercisesInWorkoutConstructor(
    userWorkoutSession: UserWorkoutSession,
    startingStage: number,
    gym: Gym,
    staticZoneNumber?: number,
  ): void {
    ScheduleExercises.scheduleExercisesInWorkout(userWorkoutSession, startingStage, gym.type, staticZoneNumber);
  }

  public static getWorkoutHydrationInfo(
    exerciseSessionsWithoutWarmupsOrPauses: UserExerciseSession[],
    gym: Gym,
    ): IWorkoutHydrationInfo {
    const numberOfExercicesForWorkout = exerciseSessionsWithoutWarmupsOrPauses.length;
    const numberOfTimesZoneTypeHasBeenDoneState: INumberOfTimesZoneTypeHasBeenDoneState = {};
    const numberOfTimesZoneHasBeenScheduledState: INumberOfTimesZoneTypeHasBeenScheduledState = {};
    this.initZoneTypeDoneState(numberOfTimesZoneTypeHasBeenDoneState, gym);
    this.initZoneTypeScheduledState(numberOfTimesZoneHasBeenScheduledState, gym);
    const zoneMappings = gym.zoneMapping;
    const zoneNumberLoopCountForStage: number[] = Array(zoneMappings.length).fill(0);
    const workoutHydrationInfo = {};
    for (let stage = 0; stage < numberOfExercicesForWorkout; stage++) {
      const zoneNumberInGym = exerciseSessionsWithoutWarmupsOrPauses[stage].zone.numberInGym - 1;
      const zoneTypeInZoneNumber =
        zoneMappings[zoneNumberInGym][zoneNumberLoopCountForStage[zoneNumberInGym]];
      const useAlternativeExercises = numberOfTimesZoneTypeHasBeenDoneState[zoneTypeInZoneNumber] % 2 > 0
        && numberOfTimesZoneTypeHasBeenDoneState[zoneTypeInZoneNumber] !==0;
      workoutHydrationInfo[stage + 1] = {
        zoneNumberInGym,
        indexOfZoneTypeInZoneNumber: zoneNumberLoopCountForStage[zoneNumberInGym],
        useAlternativeExercises,
      };
      ScheduleExercises.incremenZoneTypeState(
        numberOfTimesZoneTypeHasBeenDoneState, zoneTypeInZoneNumber);
      ScheduleExercises.findNextZoneNumberLoop(zoneNumberLoopCountForStage, zoneNumberInGym,
        gym, numberOfTimesZoneHasBeenScheduledState);
    }
    return workoutHydrationInfo;
  }

  public static setShouldUseAlternativeExercisesInStudio(exerciseSessions: UserExerciseSession[], gym: Gym) {
    if (gym.type !== GymTypes.Indoor) {
      return;
    }
    const exerciseSessionsWithoutWarmupsOrPauses = exerciseSessions.filter(
      (es) => !es.isWarmupOrWarmdown && !es.isPause,
    );
    const amountOfExerciseSessions =
      exerciseSessionsWithoutWarmupsOrPauses.length;
    const compoundWorkoutMap = ScheduleExercises.getWorkoutHydrationInfo(
      exerciseSessionsWithoutWarmupsOrPauses,
      gym,
    );
    const zoneMappings = gym.zoneMapping;
    for (let i = 0; i < amountOfExerciseSessions; i++) {
      const exerciseSession = exerciseSessionsWithoutWarmupsOrPauses[i];
      const { zoneNumberInGym: areaInGym, indexOfZoneTypeInZoneNumber: indexOfZoneInArea, useAlternativeExercises } =
        compoundWorkoutMap[i + 1];
      exerciseSession.zone.typeNumber = zoneMappings[areaInGym][indexOfZoneInArea];
      exerciseSession.shouldUseAlternativeExercisesInStudio =
        useAlternativeExercises;
    }
  }

  private static setPauseIfMatchesConfig(
      exerciseSession: UserExerciseSession,
      userWorkoutPauses: UserWorkoutPause[],
      settings: WorkoutSettings,
      gymType: GymTypes) {
    const pause = userWorkoutPauses.find((p) => p.stage === exerciseSession.workoutStage);
     // if found in config at this stage, assume its a pause
    if (pause != null) {
      const mappedPauseType = WorkoutPausesConfig.mapPauseFormatToBurnOrBuild(pause.pauseType, settings, gymType);
      exerciseSession.setPause(mappedPauseType);
    }
  }

  private static setWarmupWarmdown(
    exerciseSession: UserExerciseSession,
    amountOfStages: number,
    isCardioMachineZone: boolean,
    settings?: WorkoutSettings,
  ) {
    const workoutTypeConfig = WarmupWarmdownConfig.getFormatFor(
      amountOfStages,
      settings != null ? settings.cardioOrResistance : undefined,
      isCardioMachineZone,
    );
    if (!workoutTypeConfig) {
      return;
    }
    const exerciseFormatForSession = workoutTypeConfig.get(exerciseSession.workoutStage);
    if (!exerciseFormatForSession) {
      return;
    }
    exerciseSession.isWarmupOrWarmdown = true;
    exerciseSession.format = exerciseFormatForSession as ExerciseFormat;
  }

  private static initZoneTypeDoneState(
    numberOfTimesZoneTypeHasBeenDoneState: INumberOfTimesZoneTypeHasBeenDoneState,
    gym: Gym,
  ) {
    const allZoneTypes = gym.zoneMapping.flat();
    allZoneTypes.reduce((acc, zoneType) => {
      const zoneTypeAsString = String(zoneType);
      if (Object.keys(acc).includes(zoneTypeAsString)) {
        return acc;
      }
      acc[zoneTypeAsString] = 0;
      return acc;
    }, numberOfTimesZoneTypeHasBeenDoneState);
  }

  private static initZoneTypeScheduledState(
    numberOfTimesZoneTypeSheduledState: INumberOfTimesZoneTypeHasBeenDoneState,
    gym: Gym,
  ) {
    for (const zoneTypes of gym.zoneMapping) {
      for (const zoneType of zoneTypes) {
        const isFirstZoneType = zoneType === zoneTypes[0];
        if (isFirstZoneType) {
          if(numberOfTimesZoneTypeSheduledState[zoneType]) {
            numberOfTimesZoneTypeSheduledState[zoneType]++
            continue;
          }
          numberOfTimesZoneTypeSheduledState[zoneType] = 1;
          continue;
        }
        numberOfTimesZoneTypeSheduledState[zoneType] = 0;
      }
    }
  }

  private static incremenZoneTypeState(
    numberOfTimesZoneTypeHasBeenDoneState: INumberOfTimesZoneTypeHasBeenDoneState,
    zoneTypeNumber: number,
  ) {
    numberOfTimesZoneTypeHasBeenDoneState[zoneTypeNumber]++;
  }

  private static findNextZoneNumberLoop(
    zoneNumberLoopCountForStage: number[],
    zoneNumberInGym: number,
    gym: Gym,
    numberOfTimesZoneHasBeenScheduledState: INumberOfTimesZoneTypeHasBeenScheduledState,
    ) {

    const zoneMappings = gym.zoneMapping;
    const nextZoneNumberLoopCountForStage =
      (zoneNumberLoopCountForStage[zoneNumberInGym] + 1) %
      zoneMappings[zoneNumberInGym].length;
    const nextZoneNumberType = zoneMappings[zoneNumberInGym][nextZoneNumberLoopCountForStage];
    zoneNumberLoopCountForStage[zoneNumberInGym] = nextZoneNumberLoopCountForStage;
    let allZonetypesUsed = true;
    for(const zoneType of zoneMappings[zoneNumberInGym]) {
      if(numberOfTimesZoneHasBeenScheduledState[zoneType] < 2) {
        allZonetypesUsed = false;
        break;
      }
    }
    if(allZonetypesUsed) {
      return;
    }
    const numberOfExerciseCombosForZone = 2;
    const hasUsedAllExercisesForZoneType =
      numberOfTimesZoneHasBeenScheduledState[nextZoneNumberType] >= numberOfExerciseCombosForZone
    if (hasUsedAllExercisesForZoneType) {
      ScheduleExercises.findNextZoneNumberLoop(zoneNumberLoopCountForStage, zoneNumberInGym,
        gym, numberOfTimesZoneHasBeenScheduledState);
    } else {
      ScheduleExercises.incremenZoneTypeState(numberOfTimesZoneHasBeenScheduledState, nextZoneNumberType)
    }
  }
}
